import Icon, { IconNames } from 'components/Icon';
import { DotMark } from './DotMark';
import { TextWithFormatting } from './TextWithFormatting';
import theme from 'config/theme';
import { prompts } from './constants';
import { FC, FormEvent, useEffect, useRef } from 'react';
import { Thread } from './types';
import { getPathWithRef } from 'helpers/utils';

interface Props {
  askQuestion: (question: string) => void;
  conversationId: string;
  getOnLinkClick: (
    eventName:
      | 'Profile Dot Response Donate Link Clicked'
      | 'Profile Dot Response Inline Link Clicked',
    {
      linkUrl,
      question,
      response,
      conversationId,
    }: {
      linkUrl: string;
      question: string;
      response: string;
      conversationId: string;
    }
  ) => () => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  inputValue: string;
  isSending: boolean;
  openModalIfNeeded?: () => void;
  pendingQuestion: string | null;
  setInputValue: (value: React.SetStateAction<string>) => void;
  thread: Thread[];
}

export const LLMSectionUI: FC<Props> = ({
  askQuestion,
  conversationId,
  getOnLinkClick,
  handleSubmit,
  inputValue,
  isSending,
  openModalIfNeeded,
  pendingQuestion,
  setInputValue,
  thread,
}) => {
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [thread]);

  useEffect(() => {
    if (pendingQuestion === null && thread.length > 0 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pendingQuestion, thread.length]);

  return (
    <div className="llm-section">
      <div className="llm-intro">
        <DotMark size="lg" />
        <div>
          <div className="llm-name">Dot</div>
          <div className="llm-tagline">
            The power of the entire Oath team – all our research and recommendations at your
            fingertips.
          </div>
        </div>
      </div>

      {(thread.length > 0 || isSending) && (
        <div className="llm-thread" ref={threadRef} aria-live="polite">
          {thread.map((exchange, index) => (
            <div className="llm-exchange" key={`llm-exchange-${index}`}>
              <div className="llm-asked">{exchange.question}</div>
              <div className="llm-reply">
                <DotMark size="sm" />
                <div className="llm-reply-body">
                  <div>
                    <TextWithFormatting
                      text={exchange.answer}
                      onLinkClick={href =>
                        getOnLinkClick('Profile Dot Response Inline Link Clicked', {
                          linkUrl: href,
                          question: exchange.question,
                          response: exchange.answer,
                          conversationId,
                        })()
                      }
                    />
                  </div>
                  {!!exchange.donation_links?.length && (
                    <div className="llm-reply-donation-links">
                      {exchange.donation_links.map(link => (
                        <div key={link.route_id} className="llm-reply-donation-link">
                          <div className="llm-link-tag">
                            {link.url.includes('/cart') ? 'SEARCH CANDIDATES' : 'SLATE'}
                          </div>
                          <div className="llm-link-body">
                            <div>{link.label}</div>
                            <a
                              href={getPathWithRef(link.url)}
                              target="_blank"
                              rel="noreferrer"
                              className="llm-link-cta"
                              aria-label={`Give to ${link.label}`}
                              onClick={getOnLinkClick('Profile Dot Response Donate Link Clicked', {
                                linkUrl: link.url,
                                question: exchange.question,
                                response: exchange.answer,
                                conversationId,
                              })}
                            >
                              Give
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {isSending && (
            <div className="llm-exchange">
              <div className="llm-asked">{pendingQuestion}</div>
              <div className="llm-loading">
                <span className="llm-loading-dots">
                  <i />
                  <i />
                  <i />
                </span>
                Dot is thinking
              </div>
            </div>
          )}
        </div>
      )}

      <div className="llm-footer">
        <form className="llm-form" onSubmit={handleSubmit}>
          <input
            className="llm-input"
            ref={inputRef}
            value={inputValue}
            onChange={event => setInputValue(event.target.value)}
            onFocus={openModalIfNeeded}
            aria-label="Ask Dot anything"
            placeholder="Ask Dot anything"
            disabled={isSending}
          />
          <button className="llm-send" type="submit" aria-label="Send" disabled={isSending}>
            <Icon name={IconNames.ArrowRight} color={theme.colors.white} size={22} />
          </button>
        </form>
        <div className="llm-disclaimer">
          Responses from Dot are AI-generated and can contain mistakes.
        </div>

        {thread.length === 0 && !isSending && (
          <div className="llm-prompts">
            {prompts.map(prompt => (
              <button
                key={prompt.header}
                className="llm-prompt"
                type="button"
                disabled={isSending}
                onClick={() => {
                  askQuestion(prompt.displayText);
                  if (openModalIfNeeded) openModalIfNeeded();
                }}
              >
                <span className="eyebrow-text">{prompt.header}</span>
                <span className="llm-question">{prompt.displayText}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
